import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { Address, newAddress } from "..";
import { ClickableIcon } from "../../components/ui/Icon";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CustomPhoneInput from "../../components/ui/CustomPhoneInput";
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'
import { SelectServerSearch } from "../../components/ui/DropdownSelect";
import { SingleDatePicker } from "../../components/ui/SingleDatePicker";

interface AddressFormProps {
  item: Address;
  error?: string;
  disabled?: boolean;
  fromAndToDate?: boolean;
  autocomplete?: boolean;
  devider?: boolean;
  radioPrimary?: boolean;
  checkPrimary?: boolean;
  onRemove?: (id: number) => void;
}

export interface AddressFormRef {
  valid: () => boolean;
  getItem: () => Address;
}

export const AddressForm = forwardRef((props: AddressFormProps, ref) => {
  const {
    error,
    fromAndToDate = false,
    disabled = false,
    autocomplete = false,
    devider = false,
    radioPrimary = false,
    checkPrimary = false,
  } = props;

  const [item, setItem] = useState<Address>(newAddress);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof Address)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof Address): string => {
    if (field === "addressline1") {
      if (!item.addressline1?.trim()) return "Addresselinje 1 kan ikke være tom";
    }
    if (field === "postcode") {
      if (!item.postcode?.trim()) return "Postkode kan ikke være tom";
    }
    if (field === "postoffice") {
      if (!item.postoffice?.trim()) return "Poststed kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof Address, value: any) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleBaseBlur = (field: keyof Address) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={1.5}
        sx={{ pt: 2, justifyContent: "center", alignItems: "center" }}
      >
        <Grid item xs={(radioPrimary || checkPrimary) ? 10 : 12}>
          <TextField
            label="Adresselinje 1"
            required
            fullWidth
            disabled={disabled}
            type="text"
            autoComplete={autocomplete ? "street-address address-line1" : ""}
            value={item.addressline1}
            onChange={(e) => handleBaseChange("addressline1", e.target.value)}
            variant="outlined"
            error={validateBaseField("addressline1") !== "" && (submitted || touched.base.addressline1)}
            helperText={(submitted || touched.base.addressline1) ? validateBaseField("addressline1") : ""}
            onBlur={() => handleBaseBlur("addressline1")}
          />
        </Grid>
        {(radioPrimary || checkPrimary) && (
          <Grid item xs={props.onRemove ? 1 : 2}>
            {radioPrimary && (
              <Tooltip title="Sett til primær">
                <Radio disabled={disabled} checked={item.primary} onChange={() => handleBaseChange("primary", !item.primary)} />
              </Tooltip>
            )}
            {checkPrimary && (
              <FormControlLabel
                label={"Primær"}
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={item.primary}
                    onChange={(_, checked) => {
                      handleBaseChange("primary", checked)
                    }}
                  />
                }
              />
            )}
          </Grid>
        )}
        {props.onRemove && (
          <Grid item xs={1}>
            <ClickableIcon disabled={disabled} title="Fjern" onClick={() => props.onRemove(item.id)}>
              <ClearIcon color="error" />
            </ClickableIcon>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Adresselinje 2"
            fullWidth
            type="text"
            disabled={disabled}
            autoComplete={autocomplete ? "address-line2" : ""}
            value={item.addressline2}
            onChange={(e) => handleBaseChange("addressline2", e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Adresselinje 3"
            fullWidth
            type="text"
            disabled={disabled}
            autoComplete={autocomplete ? "address-line3" : ""}
            value={item.addressline3}
            onChange={(e) => handleBaseChange("addressline3", e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Postnummer"
            required
            fullWidth
            type="texte"
            disabled={disabled}
            autoComplete={autocomplete ? "postal-code" : ""}
            value={item.postcode}
            onChange={(e) => handleBaseChange("postcode", e.target.value)}
            variant="outlined"
            error={validateBaseField("postcode") !== "" && (submitted || touched.base.postcode)}
            helperText={(submitted || touched.base.postcode) ? validateBaseField("postcode") : ""}
            onBlur={() => handleBaseBlur("postcode")}
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            label="Poststed"
            required
            fullWidth
            type="text"
            disabled={disabled}
            autoComplete={autocomplete ? "address-level2" : ""}
            value={item.postoffice}
            onChange={(e) => handleBaseChange("postoffice", e.target.value)}
            variant="outlined"
            error={validateBaseField("postoffice") !== "" && (submitted || touched.base.postoffice)}
            helperText={(submitted || touched.base.postoffice) ? validateBaseField("postoffice") : ""}
            onBlur={() => handleBaseBlur("postoffice")}
          />
        </Grid>
        {fromAndToDate && (
          <>
            <Grid item xs={12} sm={6}>
              <SingleDatePicker
                label="Fra dato"
                fullWidth
                date={item.valid_from}
                onChange={(date) => handleBaseChange("valid_from", date)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SingleDatePicker
                label="Til dato"
                fullWidth
                disabled={disabled}
                date={item.valid_to}
                onChange={(date) => handleBaseChange("valid_to", date)}
              />
            </Grid>
          </>
        )}
      </Grid>
      {devider && <Divider sx={{ pt: 2, mt: 2, mb: 2, borderColor: 'lightgrey' }} />}
    </form>
  )
});